import * as React from 'react';

import type { OperationalCardsDto } from '@/api/operational-view-client';
import { cn } from '@/lib/utils';
import { HORS_JEUX_HINT } from '@/lib/operational-view-labels';

// =============================================================================
// operational-view-cards.tsx — Section 1 §2.5 v2.2 : indicateurs communs + bascule Profils | Fournisseurs.
// =============================================================================
// Restyling 2026-09-17 (GATE_HUMAN « simple restyling ») : la card « Indicateurs de la campagne » devient une
// LIGNE de 4 chiffres sans card, à droite de la bascule segmentée Profils | Fournisseurs. Mêmes 4 indicateurs
// (jamais agrégés en un score), mêmes valeurs, même sous-texte Hors Jeux (raccourci en ligne, texte complet en
// infobulle et pour les lecteurs d'écran). L'AUTORITÉ reste l'API : lecture du read-model `useCards`.
// Structure DOM conservée pour les ST : libellé <span> puis valeur <span> en frère immédiat.

/** Sous-texte Hors Jeux raccourci (le texte complet HORS_JEUX_HINT reste en infobulle et en sr-only). */
const HORS_JEUX_HINT_SHORT = 'Médiane du couple + seuil';

export interface OperationalCardsPanelProps {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly cards: OperationalCardsDto | undefined;
}

function Indicator({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="min-w-0">
      <span className="block font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="block text-3xl font-medium tabular-nums">{value}</span>
      {hint}
    </div>
  );
}

/**
 * Ligne d'indicateurs Section 1 (Fournisseurs invités · Cellules collectées X/Y · TJM hors jeu · Avancement du
 * cycle). Réutilisée par les pages Profils & Fournisseurs (§2.5 v2.2).
 */
export function OperationalCardsPanel({
  isPending,
  isError,
  errorMessage,
  cards,
}: OperationalCardsPanelProps): React.JSX.Element {
  return (
    <section
      aria-label="Indicateurs de la campagne"
      className="min-w-0 flex-1 lg:flex-none"
    >
      {isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Chargement des indicateurs…
        </p>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">
          Erreur de chargement : {errorMessage}
        </p>
      ) : cards === undefined ? null : (
        <div className="grid grid-cols-2 gap-x-10 gap-y-4 sm:grid-cols-4">
          <Indicator
            label="Fournisseurs invités"
            value={String(cards.invitedSupplierCount)}
          />
          <Indicator
            label="Cellules collectées"
            value={`${String(cards.collectedCellCount)} / ${String(cards.expectedCellCount)}`}
          />
          <Indicator
            label="TJM hors jeu"
            value={String(cards.horsJeuxCoupleCount)}
            hint={
              <span
                title={HORS_JEUX_HINT}
                className="block text-xs text-muted-foreground"
              >
                <span aria-hidden="true">{HORS_JEUX_HINT_SHORT}</span>
                <span className="sr-only">{HORS_JEUX_HINT}</span>
              </span>
            }
          />
          <Indicator
            label="Avancement du cycle"
            value={`${String(cards.cycleProgressPct)} %`}
          />
        </div>
      )}
    </section>
  );
}

export type OperationalViewPageId = 'profils' | 'fournisseurs';

export interface OperationalViewSwitchProps {
  readonly current: OperationalViewPageId;
  /** Navigation vers la page Profils (portée par l'adaptateur routeur). */
  readonly onViewProfils?: (() => void) | undefined;
  /** Navigation vers la page Fournisseurs (portée par l'adaptateur routeur). */
  readonly onViewFournisseurs?: (() => void) | undefined;
}

const SWITCH_OPTIONS: readonly { readonly id: OperationalViewPageId; readonly label: string }[] = [
  { id: 'profils', label: 'Profils' },
  { id: 'fournisseurs', label: 'Fournisseurs' },
];

/**
 * Bascule segmentée Profils | Fournisseurs (ex-boutons « Voir les profils / fournisseurs ») : navigue entre les
 * deux routes. Boutons aria-pressed (parité bascule SIRET | SIREN du registre), l'actif est noir.
 */
export function OperationalViewSwitch({
  current,
  onViewProfils,
  onViewFournisseurs,
}: OperationalViewSwitchProps): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label="Vue opérationnelle"
      className="inline-flex shrink-0 overflow-hidden rounded-md border border-border"
    >
      {SWITCH_OPTIONS.map((option) => {
        const active = option.id === current;
        const onNavigate =
          option.id === 'profils' ? onViewProfils : onViewFournisseurs;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={active}
            onClick={active ? undefined : onNavigate}
            className={cn(
              'h-9 px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
              active
                ? 'bg-foreground text-background'
                : 'bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** En-tête de page : titre sr-only (nom accessible conservé), bascule à gauche, indicateurs à droite. */
export function OperationalViewHeading({
  title,
  switchProps,
  cards,
}: {
  readonly title: string;
  readonly switchProps: OperationalViewSwitchProps;
  readonly cards: OperationalCardsPanelProps;
}): React.JSX.Element {
  return (
    <>
      <h2 className="sr-only">{title}</h2>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <OperationalViewSwitch {...switchProps} />
        <OperationalCardsPanel {...cards} />
      </div>
    </>
  );
}
